<template>
  <div class="space-y-6">
    <div v-if="shouldShowTabs" class="flex bg-gray-100 p-1 rounded-xl">
      <button
        @click="$emit('update:mode', 'asap')"
        :class="[
          'flex-1 py-3 px-4 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all',
          mode === 'asap'
            ? 'bg-white text-primary-600 shadow-sm'
            : 'text-gray-500 hover:text-gray-700'
        ]"
      >
        <Clock :size="18" />
        ASAP
      </button>
      <button
        @click="$emit('update:mode', 'scheduled')"
        :class="[
          'flex-1 py-3 px-4 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all',
          mode === 'scheduled'
            ? 'bg-white text-primary-600 shadow-sm'
            : 'text-gray-500 hover:text-gray-700'
        ]"
      >
        <Calendar :size="18" />
        Schedule
      </button>
    </div>

    <div
      v-if="mode === 'asap'"
      class="text-center py-8 px-4 bg-primary-50 rounded-2xl border border-primary-100"
    >
      <Clock :size="40" class="text-primary-600 mx-auto mb-3" />
      <h4 class="font-bold text-lg text-primary-900 mb-1">
        As Soon As Possible
      </h4>
      <p class="text-primary-700/80 text-sm mb-2">
        Estimated ready time: <span class="font-bold">10-15 minutes</span>
      </p>
    </div>

    <div v-else class="space-y-4 fadeIn-0-3s">
      <div>
        <label
          class="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2"
          >Date</label
        >
        <div
          v-if="availableDays.length > 0"
          class="relative days-slider-container"
        >
          <button
            v-if="canScrollLeft"
            @click="scrollDays('left')"
            class="absolute left-0 top-1 -translate-y-1/2 z-20 bg-white shadow-xl rounded-full p-2 hover:bg-gray-50 transition-all flex items-center justify-center -ml-2"
            aria-label="Scroll left"
          >
            <ChevronLeft :size="20" class="text-gray-700" />
          </button>

          <button
            v-if="canScrollRight"
            @click="scrollDays('right')"
            class="absolute right-0 top-1 -translate-y-1/2 z-20 bg-white shadow-xl rounded-full p-2 hover:bg-gray-50 transition-all flex items-center justify-center -mr-2"
            aria-label="Scroll right"
          >
            <ChevronRight :size="20" class="text-gray-700" />
          </button>

          <div
            ref="daysSlider"
            class="days-slider days-slider-mask overflow-x-auto -mx-2 px-2"
            @scroll="updateScrollButtons"
          >
            <div class="flex gap-3 min-w-max pb-1">
              <button
                v-for="day in availableDays"
                :key="day.value"
                :ref="selectedDay === day.value ? 'selectedDayButton' : null"
                @click="$emit('update:selectedDay', day.value)"
                :class="[
                  'flex-shrink-0 py-3 px-4 rounded-xl border-2 font-bold text-sm transition-all whitespace-nowrap',
                  selectedDay === day.value
                    ? 'border-primary-600 bg-primary-50 text-primary-700'
                    : 'border-gray-200 text-gray-600 hover:border-primary-200'
                ]"
              >
                {{ day.label }}
              </button>
            </div>
          </div>
        </div>
        <div v-else class="text-center py-4 text-gray-500 text-sm">
          No available days
        </div>
      </div>
      <div>
        <label
          class="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2"
          >Time</label
        >
        <div
          v-if="availableTimes.length > 0"
          class="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[52vh] overflow-y-auto pr-1"
        >
          <button
            v-for="time in availableTimes"
            :key="time"
            @click="$emit('update:selectedTime', time)"
            :class="[
              'py-4 px-3 rounded-xl text-base font-bold transition-all',
              selectedTime === time
                ? 'bg-primary-600 text-white shadow-md shadow-primary-600/20'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            ]"
          >
            {{ time }}
          </button>
        </div>
        <div v-else class="text-center py-8 text-gray-500 text-sm">
          No available times for this day
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Clock, Calendar, ChevronLeft, ChevronRight } from "lucide-vue";
import moment from "moment-timezone";

export default {
  name: "DeliverySchedulePicker",
  components: {
    Clock,
    Calendar,
    ChevronLeft,
    ChevronRight
  },
  props: {
    mode: { type: String, required: true },
    selectedDay: { type: String, default: "Today" },
    selectedTime: { type: String, default: "" },
    diningOption: { type: Object, default: null },
    storeTimezone: { type: String, default: "UTC" },
    store: { type: Object, default: null },
    scheduleOnly: { type: Boolean, default: false }
  },
  data() {
    return {
      localAvailableTimes: [],
      localAvailableDays: [],
      canScrollLeft: false,
      canScrollRight: false
    };
  },
  computed: {
    canShowSchedule() {
      return !!(this.diningOption && this.storeTimezone);
    },
    shouldShowTabs() {
      if (this.scheduleOnly) {
        return false;
      }
      return this.canShowSchedule;
    },
    availableDays() {
      return this.calculateAvailableDays();
    },
    availableTimes() {
      return this.localAvailableTimes;
    },
    storeNow() {
      return moment().tz(this.storeTimezone);
    }
  },
  watch: {
    canShowSchedule(newVal) {
      if (!newVal && this.mode === "scheduled" && !this.scheduleOnly) {
        this.$emit("update:mode", "asap");
      }
    },
    selectedDay: {
      immediate: true,
      handler() {
        if (this.mode === "scheduled") {
          this.calculateAvailableTimes();
          this.scrollToSelectedDay();
        }
      }
    },
    diningOption: {
      handler() {
        if (this.mode === "scheduled") {
          this.validateSelectedDay();
        }
      },
      deep: true
    },
    storeTimezone: {
      handler() {
        if (this.mode === "scheduled") {
          this.validateSelectedDay();
        }
      }
    },
    mode(newMode) {
      if (newMode === "scheduled") {
        this.calculateAvailableTimes();
        this.$nextTick(() => {
          this.updateScrollButtons();
        });
      }
    },
    availableDays: {
      handler(newDays) {
        if (newDays && newDays.length > 0 && this.mode === "scheduled") {
          this.validateSelectedDay();
          this.$nextTick(() => {
            this.updateScrollButtons();
          });
        }
      },
      immediate: false
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSelectedDay();
      this.updateScrollButtons();
    });
  },
  methods: {
    scrollToSelectedDay() {
      this.$nextTick(() => {
        if (this.$refs.daysSlider && this.$refs.selectedDayButton) {
          const slider = this.$refs.daysSlider;
          const selectedButton = Array.isArray(this.$refs.selectedDayButton)
            ? this.$refs.selectedDayButton[0]
            : this.$refs.selectedDayButton;

          if (slider && selectedButton) {
            const sliderRect = slider.getBoundingClientRect();
            const buttonRect = selectedButton.getBoundingClientRect();
            const scrollLeft = slider.scrollLeft;
            const buttonLeft = buttonRect.left - sliderRect.left + scrollLeft;
            const buttonWidth = buttonRect.width;
            const sliderWidth = slider.offsetWidth;

            const targetScroll = buttonLeft - sliderWidth / 2 + buttonWidth / 2;

            slider.scrollTo({
              left: targetScroll,
              behavior: "smooth"
            });
          }
        }
        setTimeout(() => {
          this.updateScrollButtons();
        }, 350);
      });
    },
    updateScrollButtons() {
      if (!this.$refs.daysSlider) {
        return;
      }

      const slider = this.$refs.daysSlider;
      const scrollLeft = slider.scrollLeft;
      const scrollWidth = slider.scrollWidth;
      const clientWidth = slider.clientWidth;

      this.canScrollLeft = scrollLeft > 0;
      this.canScrollRight = scrollLeft < scrollWidth - clientWidth - 1;
    },
    scrollDays(direction) {
      if (!this.$refs.daysSlider) {
        return;
      }

      const slider = this.$refs.daysSlider;
      const scrollAmount = 200;
      const currentScroll = slider.scrollLeft;
      const targetScroll =
        direction === "left"
          ? currentScroll - scrollAmount
          : currentScroll + scrollAmount;

      slider.scrollTo({
        left: targetScroll,
        behavior: "smooth"
      });

      setTimeout(() => {
        this.updateScrollButtons();
      }, 300);
    },
    validateSelectedDay() {
      const availableDays = this.calculateAvailableDays();
      if (availableDays && availableDays.length > 0) {
        const isCurrentDayAvailable = availableDays.find(
          (d) => d.value === this.selectedDay
        );
        if (!isCurrentDayAvailable) {
          this.$emit("update:selectedDay", availableDays[0].value);
        }
      }
    },
    calculateAvailableDays() {
      if (!this.diningOption || !this.storeTimezone) {
        return [];
      }

      const now = this.storeNow;
      const availableDaysList = [];
      const allTimings = this.diningOption.timings
        ? this.diningOption.timings.filter((timing) => !timing.is_closed)
        : [];

      if (allTimings.length === 0) {
        return [];
      }

      for (let i = 0; i < 7; i++) {
        const checkDate = now.clone().add(i, "days");
        const momentDay = checkDate.day();
        const dayOfWeek = momentDay === 0 ? 7 : momentDay;

        const hasTimingsForDay = allTimings.some(
          (timing) => timing.day_of_week === dayOfWeek
        );

        if (hasTimingsForDay) {
          let label = "";
          let value = "";
          const dateLabel = checkDate.format("MMM D");

          if (i === 0) {
            label = `Today, ${dateLabel}`;
            value = "Today";
          } else if (i === 1) {
            label = `Tomorrow, ${dateLabel}`;
            value = "Tomorrow";
          } else {
            const dayNames = [
              "Sunday",
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday"
            ];
            label = `${dayNames[momentDay]}, ${dateLabel}`;
            value = checkDate.format("YYYY-MM-DD");
          }

          availableDaysList.push({
            value,
            label,
            dayOffset: i,
            dayOfWeek,
            date: checkDate.format("YYYY-MM-DD")
          });
        }
      }

      return availableDaysList;
    },
    generateTimesFromStoreHours(targetDate, timezone) {
      if (!this.diningOption || !targetDate || !timezone) {
        return [];
      }

      const timeSlots = [];
      const allTimings = this.diningOption.timings
        ? this.diningOption.timings.filter((timing) => !timing.is_closed)
        : [];

      if (allTimings.length === 0) {
        return [];
      }

      const momentDay = targetDate.day();
      const dayOfWeek = momentDay === 0 ? 7 : momentDay;

      const timingsForDay = allTimings.filter(
        (timing) => timing.day_of_week === dayOfWeek
      );

      if (timingsForDay.length === 0) {
        return [];
      }

      timingsForDay.forEach((timing) => {
        const dayStart = targetDate.clone().startOf("day");

        const openDuration = moment.duration(timing.opening_time);
        const closeDuration = moment.duration(timing.closing_time);

        const openTime = dayStart.clone().add(openDuration);
        const closeTime = dayStart.clone().add(closeDuration);

        if (closeTime.isBefore(openTime) || closeTime.isSame(openTime)) {
          closeTime.add(1, "day");
        }

        let currentSlot = openTime.clone();
        while (currentSlot.isBefore(closeTime)) {
          const timeString = currentSlot.format("h:mm A");
          if (!timeSlots.includes(timeString)) {
            timeSlots.push(timeString);
          }
          currentSlot = currentSlot.add(30, "minutes");
        }
      });

      return timeSlots.sort((a, b) => {
        const timeA = moment(a, "h:mm A");
        const timeB = moment(b, "h:mm A");
        return timeA.diff(timeB);
      });
    },
    calculateAvailableTimes() {
      let uniqueSlots = [];

      if (!this.diningOption || !this.storeTimezone) {
        this.localAvailableTimes = [];
        return;
      }

      const timezone = this.storeTimezone;
      const now = this.storeNow;

      let targetDate;
      if (this.selectedDay === "Today") {
        targetDate = now.clone();
      } else if (this.selectedDay === "Tomorrow") {
        targetDate = now.clone().add(1, "days");
      } else {
        targetDate = moment(this.selectedDay, "YYYY-MM-DD").tz(timezone);
        if (!targetDate.isValid()) {
          this.localAvailableTimes = [];
          return;
        }
      }

      const isToday = targetDate.isSame(now, "day");

      uniqueSlots = this.generateTimesFromStoreHours(targetDate, timezone);

      if (isToday && uniqueSlots.length > 0) {
        const currentTime = now.clone();
        const bufferMinutes = 30;

        uniqueSlots = uniqueSlots.filter((timeString) => {
          const slotTime = moment(timeString, "h:mm A");
          slotTime.set({
            year: currentTime.year(),
            month: currentTime.month(),
            date: currentTime.date()
          });

          return slotTime.isAfter(
            currentTime.clone().add(bufferMinutes, "minutes")
          );
        });
      }

      this.localAvailableTimes = uniqueSlots;

      if (uniqueSlots.length > 0) {
        if (
          !this.selectedTime ||
          this.selectedTime.trim() === "" ||
          !uniqueSlots.includes(this.selectedTime)
        ) {
          if (uniqueSlots[0] !== this.selectedTime) {
            this.$emit("update:selectedTime", uniqueSlots[0]);
          }
        }
      } else if (this.selectedTime && this.selectedTime.trim() !== "") {
        this.$emit("update:selectedTime", "");
      }
    }
  }
};
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
.fadeIn-0-3s {
  animation: fadeIn 0.3s ease-out;
}

.days-slider-mask {
  mask-image: linear-gradient(
    to right,
    transparent 0px,
    black 20px,
    black calc(100% - 20px),
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0px,
    black 20px,
    black calc(100% - 20px),
    transparent 100%
  );
}

.days-slider {
  scrollbar-width: none;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
}

.days-slider::-webkit-scrollbar {
  display: none;
}
</style>
